import { Component, createRef } from 'react';
import PropTypes from 'prop-types';

import s from './LookerDashboardView.module.scss';

// Looker postMessage events of interest (see Spike 1548805 §4.4). Using the
// Embed Message API rather than @looker/embed-sdk for now: the SDK's published
// dependency "@typescript/lib-dom": "npm:@types/web" fails Yarn 4 resolution,
// and the iframe receives the same named events over postMessage. Revisit once
// the alias is fixed upstream (or the monorepo upgrades Yarn).
const EVENTS = {
    dashboardLoaded: 'dashboard:loaded',
    sessionStatus: 'session:status',
};

const lookerOriginOf = embedUrl => {
    try {
        return `https://${new URL(embedUrl).host}`;
    } catch {
        return undefined;
    }
};

/*
 * Renders the Looker Spatial Insights dashboard inside an iframe using the
 * signed embed URL scoped to the caller venues (PBI 1610410). Venue isolation
 * is enforced server side by the signed allowed_venues user attribute; this
 * mirrors the Power BI embed pattern (single iframe, event-driven spinner,
 * session-expiry surfaced to the parent).
 */
class LookerDashboardView extends Component {
    static propTypes = {
        embedUrl: PropTypes.string,
        loading: PropTypes.bool,
        /** Signals the parent that the embed session expired (fresh URL on next entry). */
        onSessionExpired: PropTypes.func,
    };

    static defaultProps = { embedUrl: '', loading: false, onSessionExpired: () => {} };

    iframeRef = createRef();
    state = { ready: false };

    handleMessage = message => {
        const { embedUrl, onSessionExpired } = this.props;
        if (!embedUrl) return;
        if (message.origin !== lookerOriginOf(embedUrl)) return;
        if (message.source !== this.iframeRef?.current?.contentWindow) return;
        const type = message.data?.type || message.type;
        if (type === EVENTS.dashboardLoaded) this.markReady('dashboard:loaded');
        if (type === EVENTS.sessionStatus && message.data?.expired) onSessionExpired({ source: 'looker' });
    };

    componentDidMount() {
        window.addEventListener('message', this.handleMessage);
    }

    componentWillUnmount() {
        window.removeEventListener('message', this.handleMessage);
        window.clearTimeout(this.fallbackTimer);
    }

    /** Secondary signal: once the iframe document itself settles (`load` + brief grace
     * for async tiles), drop the loader even if the Looker postMessage never fires
     * (event vocabulary varies across Looker versions; the overlay must not trap). */
    markReady = () => {
        if (this.state.ready) return;
        window.clearTimeout(this.fallbackTimer);
        this.setState({ ready: true });
    };

    handleIframeLoad = () => {
        window.clearTimeout(this.fallbackTimer);
        this.fallbackTimer = window.setTimeout(() => {
            if (!this.state.ready) this.markReady('iframe:load-fallback');
        }, 1500);
    };

    render() {
        const { embedUrl } = this.props;
        const { ready } = this.state;
        if (!embedUrl) return null;
        return (
            <div className={s.lookerDashboardView}>
                <iframe
                    ref={this.iframeRef}
                    title='Spatial Insights'
                    data-testid='looker-iframe'
                    src={embedUrl}
                    className={s.embed}
                    allow='fullscreen'
                    onLoad={this.handleIframeLoad}
                />
                {!ready && (
                    <div className={s.loader} data-testid='looker-embed-loading'>
                        {'Loading dashboard...'}
                    </div>
                )}
            </div>
        );
    }
}

export default LookerDashboardView;
